<template>
  <div>
    <aha-section>
      <template #header>
        <h5 v-if="!data.oneTime">
          {{ $i18nPresenter.t('Current plan') }}
        </h5>
        <h5 v-else>
          {{ $i18nPresenter.t('On-going plan') }}
        </h5>
      </template>
      <template #body>
        <table class="table-plan">
          <colgroup>
            <col class="w-1/2 md:w-1/3">
            <col class="w-1/2 md:w-2/3">
          </colgroup>
          <tbody class="w-full">
            <tr
              v-if="data.subscription.plan.group != 'pro'
                && data.subscription.plan.group != 'white-label'"
            >
              <td colspan="2" class="py-2 italic">
                {{ $i18nPresenter.t('You can upgrade to AhaSlides Pro to host bigger events, have customised branding and more!') }}
              </td>
            </tr>
            <tr>
              <td class="table-plan-property">
                <i class="mr-1 fas fa-cart-arrow-down fa-fw" />
                {{ $i18nPresenter.t('Your plan:') }}
              </td>
              <td
                class="py-2 font-bold table-plan-value"
                :class="getPlanColor(data.subscription.plan)"
              >
                {{ data.subscription.plan.name }}
              </td>
            </tr>
            <tr>
              <td class="table-plan-property">
                <i class="mr-1 fas fa-user fa-fw" />
                {{ $i18nPresenter.t('Owner:') }}
              </td>
              <td class="py-2 table-plan-value">
                {{ currentUser.email }}
              </td>
            </tr>
            <tr>
              <td class="table-plan-property">
                <i class="mr-1 fas fa-users fa-fw" />
                {{ $i18nPresenter.t('Participant limit:') }}
              </td>
              <td class="py-2 table-plan-value">
                {{ data.subscription.plan.limit | formatNumber }}
              </td>
            </tr>
            <tr v-if="data.subscription && data.subscription.current_period_start">
              <td class="table-plan-property">
                <i class="mr-1 far fa-calendar-alt fa-fw" />
                {{ $i18nPresenter.t('Start date:') }}
              </td>
              <td class="py-2 table-plan-value">
                {{ data.subscription.current_period_start | dateTimeStamp }}
              </td>
            </tr>
            <tr v-if="data.subscription">
              <td class="table-plan-property">
                <i class="mr-1 far fa-calendar-check fa-fw" />
                <span
                  v-if="data.subscription.status == 'active' &&
                    !data.subscription.cancel_at_period_end"
                >
                  {{ $i18nPresenter.t('Next billing date:') }}
                </span>
                <span v-else>
                  {{ $i18nPresenter.t('End date:') }}
                </span>
              </td>
              <td class="py-2 table-plan-value">
                {{ data.subscription.current_period_end | dateTimeStamp }}
              </td>
            </tr>
            <tr
              v-if="data.subscription && $store.state.plan.type != 'white-label'
                && data.subscription.amount"
            >
              <td class="capitalize table-plan-property">
                <i class="mr-1 fas fa-money-check-alt fa-fw" />
                {{ $i18nPresenter.t(`${data.subscription.plan.billingCycle} cost:`) }}
              </td>
              <td class="py-2 table-plan-value" v-if="isDisplayingNextInvoicePromotion">
                <span
                  class="pr-1 line-through sub-has-coupon text-base-60"
                >{{ data.subscription.amount }} {{ getCurrency }}</span>
                <span>{{ promotionOnCancelAmount }} {{ getCurrency }}</span>
                <span
                  v-if="isDisplayingNextInvoicePromotion"
                >
                  <aha-badge
                    :label="`${promotionOnCancelPercent}% OFF`"
                    class="inline mx-2 bg-green-50"
                  />
                  <span
                    class="cursor-pointer fas fa-question-circle text-secondary-40"
                    v-tooltip="promotionOnCancelTooltip"
                  />
                </span>
              </td>
              <td class="py-2 table-plan-value" v-else>
                {{ data.subscription.amount }} {{ getCurrency }}
                <p v-if="$store.state.plan.oldPlan" class="text-xs">
                  {{ $i18nPresenter.t(`Although the price for this plan has increased after you subscribed, you are still paying the old price for as long as your subscription goes.`) }}
                </p>
              </td>
            </tr>
            <tr
              v-if="data.subscription && data.subscription.cancel_at_period_end"
            >
              <td class="table-plan-property">
                <i class="mr-1 fas fa-undo-alt fa-fw" />
                {{ $i18nPresenter.t('Auto-renewal:') }}
              </td>
              <td class="py-2 table-plan-value">
                <span>
                  {{ $i18nPresenter.t('Off') }} <span v-if="!autoRenewOn && !isIDealSubscription">
                    (<a
                      class="autorenewing-text"
                      v-if="isTurningOnAutoRenew"
                    >{{ $i18nPresenter.t('turning on...') }}</a>
                    <a
                      class="text-link--muted"
                      v-else
                      @click="$emit('handleTurnOnAutoRenew')"
                    >{{ $i18nPresenter.t('turn on') }}</a>)
                  </span>
                </span>
              </td>
            </tr>
            <tr
              v-if="data.subscription && data.subscription.coupon &&
                $store.state.plan.type != 'white-label'"
            >
              <td class="table-plan-property">
                <i class="mr-1 fas fa-gift fa-fw" />
                {{ $i18nPresenter.t('Discount applied:') }}
              </td>
              <td class="py-2 table-plan-value">
                <div>
                  <span
                    :class="{'sub-has-coupon line-through pr-1': data.subscription.coupon}"
                  >{{ data.subscription.amount }} {{ getCurrency }}</span>
                  <span
                    v-if="data.subscription.coupon"
                    class="font-bold"
                  >{{ data.subscription.coupon.amount }} {{ getCurrency }}</span>
                </div>
                <div
                  class="text-xs label-coupon text-base-80"
                >
                  {{ $i18nPresenter.t('Promo code:') }} <span
                    v-if="!data.subscription.coupon.metadata ||
                      data.subscription.coupon.metadata.offer_on_cancellation !== 'true'"
                  >{{ data.subscription.coupon.id }} -</span>
                  {{ data.subscription.coupon.name }}
                </div>
                <div
                  class="text-xs label-coupon text-base-80"
                  v-if="!data.subscription.coupon.metadata ||
                    data.subscription.coupon.metadata.offer_on_cancellation !== 'true'"
                >
                  {{ $i18nPresenter.t('Duration:') }} {{ data.subscription.coupon.duration }}
                  <span
                    v-if="data.subscription.coupon.duration == 'repeating'"
                  >({{ data.subscription.coupon.duration_in_months }} {{ $i18nPresenter.t('months') }})</span>
                </div>
              </td>
            </tr>
            <tr v-if="data.card">
              <td class="align-top table-plan-property">
                <i class="mr-1 far fa-credit-card fa-fw" />
                {{ $i18nPresenter.t('Payment method:') }}
              </td>
              <td class="py-2 table-plan-value">
                <strong class="uppercase">{{ data.card.brand }}</strong> •••• •••• ••••
                <strong>{{ data.card.last4 }}</strong>
                <br>
                <span v-if="data.subscription" class="small-90">
                  <span
                    class="text-link--muted"
                    @click="$emit('showPopupUpdateCardInfo')"
                  >{{ $i18nPresenter.t('Update') }}</span> /
                </span>
                <span
                  class="text-link--muted"
                  @click="$emit('removeCardInfo')"
                >{{ $i18nPresenter.t('Remove') }}</span>
              </td>
            </tr>
            <tr
              v-if="data.subscription &&
                data.subscription.current_period_start &&
                !data.subscription.cancel_at_period_end"
            >
              <td colspan="2" class="py-2 text-sm text-left">
                <p>
                  {{ $i18nPresenter.t(`We will send you a reminder email 2 weeks before your subscription is renewed. To cancel your subscription now, please`) }}
                  <span
                    class="cursor-pointer text-base-80 hover:text-primary-90"
                    @click="handleClickOnCancelSub()"
                  >{{ $i18nPresenter.t('click here') }}</span>.
                </p>
              </td>
            </tr>
            <tr>
              <td class="table-plan-property">
                <aha-icon name="coin" class="-mt-0.5 -ml-0.5" />
                {{ $i18nPresenter.t('Credit balance:') }}
              </td>
              <td
                class="py-2 table-plan-value"
              >
                <span v-if="currentUser.balance">
                  {{ (0 - currentUser.balance)/100 | floatNumber }} {{ getCurrency }}
                </span>
                <span v-else>0.00 {{ getCurrency }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </template>
      <template
        #footer
        v-if="showUpgradePlanFooter"
      >
        <div class="h-13 p-3 bg-yellow-10 text-yellow-100">
          <span>{{ $i18nPresenter.t('Upgrading your plan here will not affect your team plan.') }}</span>
        </div>
      </template>
    </aha-section>
    <modal-cancel-sub-info
      v-if="isCancelSubInfo"
      @close="isCancelSubInfo = false"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import storageKeys from '@/constant/storageKeys'
import ModalCancelSubInfo from '../modals/CancelSubInfo.vue'
import Util from '@/util'

export default {
  components: {
    ModalCancelSubInfo,
  },
  props: {
    data: {
      type: Object,
      default: () => {},
    },
    isDisplayingNextInvoicePromotion: {
      type: Boolean,
      default: false,
    },
    autoRenewOn: {
      type: Boolean,
      default: false,
    },
    isTurningOnAutoRenew: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isCancelSubInfo: false,
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
    }),
    showUpgradePlanFooter() {
      if (this.$store.state.currentUser.accountId
        && this.$store.state.currentUser.accountRole === 'owner'
        && this.$store.state.currentAccount.createdFrom === 'presenter') return true
      return false
    },
    promotionOnCancelAmount() {
      if (!this.data.discount || !this.data.discount.coupon ||
      !this.data.discount.coupon.percent_off || !this.data.subscription) return null
      return Math.round((100 - this.data.discount.coupon.percent_off)
      * this.data.subscription.amount) / 100
    },
    promotionOnCancelPercent() {
      return this.data.discount &&
         this.data.discount.coupon &&
        this.data.discount.coupon.percent_off
    },

    promotionOnCancelTooltip() {
      return this.$i18nPresenter.t('thisExclusiveOfferTakesXPercentOff', {
        percent: this.promotionOnCancelPercent,
      })
    },
    getCurrency() {
      if (this.currentUser.currency === 'eur') return 'EUR'
      return 'USD'
    },
    isIDealSubscription() {
      if (this.currentUser.currency === 'eur' && this.currentUser.plan !== 'free') return true
      return false
    },
  },
  methods: {
    getPlanColor(subscription) {
      this.$emit('getPlanColor', subscription)
    },
    handleClickOnCancelSub() {
      if (this.isDisplayingNextInvoicePromotion) {
        this.$emit('showPromotionOnCancel')
        return
      }
      localStorage.setItem(storageKeys.IS_REMOVE_CARD_PURPOSE, false)
      const oldPlan = Util.isSubscribleOldPlan(this.currentUser.plan)
      if (oldPlan) this.isCancelSubInfo = true
      else this.$router.push({ name: 'UserCancelSub' })
    },
  },
}
</script>

<style lang="scss" scoped>
.table-plan {
  @apply w-full;
}
.table-plan-property {
  @apply font-bold py-2 w-1/2 md:w-1/3 align-top;
}
.table-plan-value {
  @apply py-2 w-1/2 md:w-2/3 align-top;
  word-break: break-word;
}
</style>
